/** Settings sections: the type, the sidebar groups and the pure helpers (search, order, aliases). */
import type { ComponentType } from 'react';

/** Where a section sits in the Settings sidebar, in this order. */
export const SETTINGS_GROUPS = [
  { id: 'workspace', label: 'Workspace' },
  { id: 'agents', label: 'Agents and approvals' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'alerts', label: 'Alerts' },
  { id: 'money', label: 'Money' },
  { id: 'security', label: 'Security' },
  { id: 'browser', label: 'This browser' },
] as const;

export type SettingsGroup = (typeof SETTINGS_GROUPS)[number]['id'];

/**
 * A Settings section. Each feature adds one file in ./sections/ that default-exports a
 * SettingsSection; Settings finds them all, so no shared list needs editing.
 */
export interface SettingsSection {
  /** URL segment: /settings/<id>. */
  id: string;
  title: string;
  /** One line under the title. */
  description: string;
  /** Lower comes first within its group. */
  order: number;
  /** The sidebar group. Sections without one go under Workspace. */
  group?: SettingsGroup;
  /** Extra words the settings search and the command palette match ("auto-approve"). */
  keywords?: string[];
  icon: ComponentType<{ className?: string }>;
  Component: ComponentType;
  /**
   * The module (or route) the section needs. On Orgus Cloud without it, the section shows that
   * the feature is coming and `instead` (what works today), and asks the server nothing.
   */
  needs?: { module: string; feature: string; plural?: boolean; instead: string };
}

/** By group (in SETTINGS_GROUPS order), then by order, then by title. */
export function sortSections(list: SettingsSection[]): SettingsSection[] {
  const rank = (s: SettingsSection) => SETTINGS_GROUPS.findIndex((g) => g.id === (s.group ?? 'workspace'));
  return [...list].sort((a, b) => rank(a) - rank(b) || a.order - b.order || a.title.localeCompare(b.title));
}

/** Old and plain-language addresses that open a section: /settings/approvals opens the approval rules. */
export const SETTINGS_ALIASES: Record<string, string> = {
  approvals: 'policies',
  'approval-rules': 'policies',
  permissions: 'policies',
  'auto-approve': 'policies',
  rules: 'policies',
};

/** Sections whose title, description or keywords contain every word of the query, in sidebar order. */
export function filterSections(list: SettingsSection[], query: string): SettingsSection[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return list;
  return list.filter((s) => {
    const hay = [s.title, s.description, ...(s.keywords ?? [])].join(' ').toLowerCase();
    return words.every((w) => hay.includes(w));
  });
}

/** Sections grouped for the sidebar; empty groups are left out. */
export function groupSections(list: SettingsSection[]): { id: SettingsGroup; label: string; sections: SettingsSection[] }[] {
  return SETTINGS_GROUPS.map((g) => ({ id: g.id, label: g.label, sections: list.filter((s) => (s.group ?? 'workspace') === g.id) })).filter((g) => g.sections.length > 0);
}
